(ns dashboard.entity.question.db
  (:require [clojure.string :as str]
            [dashboard.components.paginated-list.view :as-alias paginated-list]
            [dashboard.entity.analysis.db :as analysis.db]
            [dashboard.notifications :as notifications]
            [dashboard.services.answers :as answers]
            [dashboard.services.http-config :as http-config]
            [dashboard.services.question-note :as question-note]
            [dashboard.services.questionnaire-list :as questionnaire-list]
            [dashboard.services.questionnaires :as questionnaires]
            [dashboard.subs :as common-subs]
            [dashboard.ui :as ui]
            [io.repliance.answer-data :as answer-data]
            [io.repliance.schemas.core :as schemas]
            [io.repliance.util.tree :as tree]
            [re-frame.core :as rf]))

;; ----------------------------------------------------------------------------
;;  Utility Functions
;; ----------------------------------------------------------------------------

(def question-list--app-db-key :view/question-list)

(def question-list--view-id
  :route.view/questionnaire-question-list)

(defn filter->pred
  ([filter]
   (filter->pred nil filter))
  ([metadata [filter-name filter-value]]
   (if filter-value
     (case filter-name
       "section" #(= (:section-id %) (:id filter-value))
       "status/approved" #(= (:status %) "approved")
       "status/in-review" #(= (:status %) "in-review")
       "status/unknown" #(= (:status %) nil)
       "unread-notes" (fn [question]
                        (> (get-in metadata [:unread-notes (:id question)]) 0))
       #_:else (constantly false))
     (constantly true))))

(defn filters->pred [filters metadata]
  (let [{:strs [status section unread-notes]} (group-by #(first (str/split (first %) #"/"))
                                                        filters)
        status-pred (if status
                      (apply some-fn (map filter->pred status))
                      (constantly true))
        section-pred (if section
                       (apply some-fn (map filter->pred section))
                       (constantly true))
        unread-notes-pred (if unread-notes
                            (apply some-fn (map (partial filter->pred metadata) unread-notes))
                            (constantly true))]
    (every-pred status-pred
                unread-notes-pred
                section-pred)))

(defn build-tree [questions pred]
  (let [questions (tree/sort-hierarchical questions nil nil)]
    (keep (fn [question]
            (if (pred question)
              question
              (let [root-id (tree/find-root-id (:id question) questions)
                    subtree (tree/filter-tree root-id questions)
                    subtree-matches (filter pred subtree)]
                (when (seq subtree-matches)
                  (if (and (not (pred question))
                           (seq subtree-matches))
                    (assoc question :disabled true)
                    question)))))
          questions)))


;; ----------------------------------------------------------------------------
;;  Subscriptions and their Getters
;; ----------------------------------------------------------------------------

;; ----------------------------------------------------------------------------
;; ∆∆ Top-level Data dependancies
;; ----------------------------------------------------------------------------

(rf/reg-sub
 ::question-list-view-config
 (fn [db] (:question-list db)))

(rf/reg-sub
 ::question-list
 (fn [db]
   (get db question-list--app-db-key)))

(rf/reg-sub
 ::comparison-control
 :<- [::question-list]
 (fn [question-list] (get question-list :comparison-control)))

(rf/reg-sub
 ::analysis-suggestions
 :<- [::question-list]
 (fn [question-list]
   (get question-list :analysis-suggestions)))

(rf/reg-sub
 ::analysis-suggestions-data
 :<- [::analysis-suggestions]
 (fn [analysis-suggestions [_ questionnaire-id]]
   (get-in analysis-suggestions [questionnaire-id :data])))

(rf/reg-sub
 ::comparison-suggestions-data
 (fn [db]
   (:comparison-suggestions db)))

(rf/reg-sub
 ::questionnaire-comparison-suggestions
 :<- [::comparison-suggestions-data]
 (fn [comparison-suggestions [_ questionnaire-id]]
   (get comparison-suggestions questionnaire-id)))

;; ----------------------------------------------------------------------------

(rf/reg-sub
 ::question-filter
 :<- [::question-list-view-config]
 :<- [::question-note/question-notes-db]
 (fn [[question-list question-notes] [_ questionnaire-id]]
   (let [metadata {:unread-notes (get-in question-notes [questionnaire-id :unread])}
         filters (get-in question-list [:filters questionnaire-id])]
     (if-not (empty? filters)
       (filters->pred filters metadata)
       (constantly true)))))

(defn question-filter*
  [questionnaire-id]
  (rf/subscribe [::question-filter questionnaire-id]))

;; ----------------

(rf/reg-sub
 ::lab-question-ids
 (fn [[_ questionnaire-id]]
   [(rf/subscribe [::questions questionnaire-id])])
 (fn [[questions]]
   (->> questions
        (remove :disabled)
        (map :id))))

(defn lab-question-ids*
  [questionnaire-id]
  (rf/subscribe [::lab-question-ids questionnaire-id]))

;; ----------------

(rf/reg-sub
 ::filter-popup-visible
 :<- [::question-list-view-config]
 (fn [question-list-view-config [_]]
   (:filter-popup-visible question-list-view-config false)))

(defn filter-popup-visible*
  []
  (rf/subscribe [::filter-popup-visible]))

;; ----------------

(rf/reg-sub
 ::filters
 :<- [::question-list-view-config]
 (fn [question-list-view-config [_ questionnaire-id]]
   (get-in question-list-view-config [:filters questionnaire-id])))

(defn filters*
  [questionnaire-id]
  (rf/subscribe [::filters questionnaire-id]))

;; ----------------

(rf/reg-sub
 ::comparison-control-visible
 :<- [::comparison-control]
 (fn [comparison-control [_ questionnaire-id]]
   (get-in comparison-control [questionnaire-id :visible?] false)))

(defn comparison-control-visible*
  [questionnaire-id]
  (rf/subscribe [::comparison-control-visible questionnaire-id]))

;; ----------------

(rf/reg-sub
 ::comparisons
 :<- [::comparison-control]
 (fn [comparison-control [_ questionnaire-id]]
   (get-in comparison-control [questionnaire-id :comparisons] [])))

(defn comparisons*
  [questionnaire-id]
  (rf/subscribe [::comparisons questionnaire-id]))

;; ----------------

(rf/reg-sub
 ::analysis-suggestions-visible
 :<- [::analysis-suggestions]
 (fn [analysis-suggestions [_ questionnaire-id]]
   (get-in analysis-suggestions [questionnaire-id :visible] false)))

(defn analysis-suggestions-visible*
  [questionnaire-id]
  (rf/subscribe [::analysis-suggestions-visible questionnaire-id]))

;; ----------------

(rf/reg-sub
 ::analysis-suggestions-loading
 :<- [::analysis-suggestions]
 (fn [analysis-suggestions [_ questionnaire-id]]
   (get-in analysis-suggestions [questionnaire-id :loading] false)))

(defn analysis-suggestions-loading*
  [questionnaire-id]
  (rf/subscribe [::analysis-suggestions-loading questionnaire-id]))

;; ----------------

(rf/reg-sub
 ::comparison-suggestions
 :<- [::comparison-suggestions-data]
 (fn [comparison-suggestions [_ questionnaire-id target-questionnaire-id]]
   (get-in comparison-suggestions [questionnaire-id target-questionnaire-id])))

(defn comparison-suggestions*
  [questionnaire-id target-questionnaire-id]
  (rf/subscribe [::comparison-suggestions questionnaire-id target-questionnaire-id]))

;; ----------------

;; Only used by ::questions
(rf/reg-sub
 ::latest-analyses
 :<- [::analysis.db/structured-analyses-data]
 :<- [::analysis.db/applied-analyses-data]
 (fn [[structured-analyses applied-analyses]]
   (into {}
         (map (fn [[construct-id structured-analyses]]
                (let [latest-structured-analysis (first structured-analyses)
                      latest-applied-analysis (first (get applied-analyses construct-id))]
                  [construct-id (assoc latest-structured-analysis :applied-analysis latest-applied-analysis)])))
         structured-analyses)))

;; Only used by ::questions
(rf/reg-sub
 ::structured-analysis-questions
 (fn [db]
   (:structured-analysis-questions db)))

(rf/reg-sub
 ::questions
 (fn [[_ questionnaire-id]]
   [(rf/subscribe [::common-subs/features-enabled])
    (rf/subscribe [::comparisons questionnaire-id])
    (rf/subscribe [::analysis-suggestions-visible questionnaire-id])
    (rf/subscribe [::analysis-suggestions-data questionnaire-id])
    (rf/subscribe [::latest-analyses])
    (rf/subscribe [::questionnaire-list/data])
    (rf/subscribe [::questionnaire-comparison-suggestions questionnaire-id])
    (rf/subscribe [::questionnaires/questions questionnaire-id])
    (rf/subscribe [::structured-analysis-questions])
    (analysis.db/structured-analyses-data*)
    (analysis.db/answer-id->applied-analyses*)
    (question-filter* questionnaire-id)])
 (fn [[features-enabled
       comparisons
       analysis-suggestions-visible
       analysis-suggestions
       latest-analyses
       questionnaire-list
       questionnaire-comparison-suggestions
       questions
       structured-analysis-questions
       structured-analyses-data
       answer-id->applied-analyses
       question-filter]]
   (let [comparison-targets (remove empty? comparisons)
         comparison-targets-selected? (seq comparison-targets)]
     (mapcat
      (fn [question]
        (let [question (-> question
                           (assoc :view/features-enabled features-enabled)
                           (assoc :paginated-list/row-key (:id question)))]
          (cond-> [(assoc question :comparison-mode (or comparison-targets-selected?
                                                        analysis-suggestions-visible))]

            analysis-suggestions-visible
            (into [(assoc question
                          :suggestion-type :analysis
                          :suggestion {:structured-analyses
                                       (map (fn [construct-id]
                                              (get latest-analyses construct-id
                                                   ;; Support suggestion for SA that is not yet present in the client environment
                                                   {:construct-id construct-id}))
                                            (get analysis-suggestions (:id question)))}
                          :last-comparison-item (not comparison-targets-selected?))])

            comparison-targets-selected?
            (into (keep (fn [[idx {:keys [short-id target-questionnaire] :as comparison}]]
                          (when-not (empty? comparison)
                            (let [target-questionnaire-id (:id target-questionnaire)
                                  target-questionnaire (first (filter #(= target-questionnaire-id (:id %)) questionnaire-list))
                                  suggested-question (get-in questionnaire-comparison-suggestions [target-questionnaire-id :data (:id question)])
                                  suggested-structured-analyses (map (fn [construct-id]
                                                                       (get latest-analyses construct-id
                                                                            ;; Support suggestion for SA that is not yet present in the client environment
                                                                            {:construct-id construct-id}))
                                                                     (:structured-analyses suggested-question))]
                              (assoc question
                                     :suggestion-type :questionnaire
                                     :short-id short-id
                                     :suggestion (assoc suggested-question :structured-analyses suggested-structured-analyses)
                                     :target-questionnaire target-questionnaire
                                     :last-comparison-item (= idx (dec (count comparison-targets)))
                                     ::paginated-list/hide-row-selection true)))))
                  (map-indexed vector comparison-targets)))))
      (let [questions (map (fn [question]
                             (let [question
                                   (if-let [{answer-id :id} (:selected-answer question)]
                                     (let [applied-analyses
                                           (->> (answer-id->applied-analyses answer-id)
                                                (sort ;; Compare id first, ascending order, version number second, descending order
                                                 #(compare [(:construct-id %1)
                                                            (:applied-structured-analysis-id %2)]
                                                           [(:construct-id %2)
                                                            (:applied-structured-analysis-id %1)]))
                                                (map (fn [{:keys [construct-id
                                                                  structured-analysis-version-uuid]
                                                           :as applied-analysis}]
                                                       (assert construct-id)
                                                       (let [all-versions (get structured-analyses-data construct-id)
                                                             structured-analysis (first (filter #(= structured-analysis-version-uuid (:version-id %)) all-versions))]
                                                         (assoc applied-analysis :structured-analysis structured-analysis)))))]
                                       (assoc-in question [:selected-answer :applied-analyses] applied-analyses))
                                     question)]
                               (assoc question
                                      :structured-analyses
                                      (if-let [linked-structured-analyses (get-in structured-analysis-questions [:data (:id question)])]
                                               ;; Note that we do store the exact version linked, but it is probably enough to show data from latest version only
                                        (map (fn [{:keys [construct-id]}]
                                               (get structured-analyses-data construct-id))
                                             linked-structured-analyses)
                                        []))))
                           questions)]
        (build-tree questions (or question-filter (constantly true))))))))

(defn questions*
  [questionnaire-id]
  (rf/subscribe [::questions questionnaire-id]))

;; ----------------

(rf/reg-sub
 ::loading-questions
 :<- [::questionnaires/questionnaires]
 :<- [::analysis.db/structured-analyses]
 :<- [::analysis.db/applied-analyses]
 (fn [[questionnaires structured-analyses applied-analyses] [_ questionnaire-id]]
   (or (get-in questionnaires [questionnaire-id :questions :loading])
       (get structured-analyses :loading)
       (get applied-analyses :loading))))

(defn loading-questions*
  [questionnaire-id]
  (rf/subscribe [::loading-questions questionnaire-id]))

;; ----------------

(rf/reg-sub
 ::column-visible?
 :<- [::question-list]
 (fn [question-list [_ column-id]]
   (get-in question-list [:column-visibility column-id] true)))

(defn column-visible?*
  [column-id]
  (rf/subscribe [::column-visible? column-id]))

;; ----------------------------------------------------------------------------
;;  Effects and their Callers
;; ----------------------------------------------------------------------------

(rf/reg-event-db
 ::get-sections-success
 (fn [db [_ questionnaire-id sections]]
   (-> db
       (assoc-in [:questionnaires questionnaire-id :sections :loading] false)
       (assoc-in [:questionnaires questionnaire-id :sections :data] sections)
       (update-in [:question-list :filters questionnaire-id]
                  (fn [filters]
                    (or filters {"section" (first (sort-by :ordinal sections))}))))))

(rf/reg-event-fx
 ::get-sections-failure
 (fn [{:keys [db]} [_ questionnaire-id]]
   {:dispatch [::ui/set-error "Failed to fetch sections"]
    :db (assoc-in db [:questionnaires questionnaire-id :sections :loading] false)}))

(rf/reg-event-fx
 ::get-sections
 (fn [{:keys [db]} [_ questionnaire-id]]
   {:db (assoc-in db [:questionnaires questionnaire-id :sections :loading] true)
    :http-xhrio {:method :get
                 :uri (str "/api/questionnaires/" questionnaire-id "/sections")
                 :format http-config/transit-request-format
                 :response-format http-config/transit-response-format
                 :on-success [::get-sections-success questionnaire-id]
                 :on-failure [::get-sections-failure]}}))

(defn get-sections
  [questionnaire-id]
  (rf/dispatch [::get-sections questionnaire-id]))

;; ----------------

(rf/reg-event-fx
 ::update-questionnaire-status-success
 (fn [{:keys [db]} [_ questionnaire status]]
   {:db (assoc-in db
                  [:questionnaires questionnaire :metadata :data :status]
                  status)
    :dispatch [::notifications/add {:message "Questionnaire status updated"}]}))

(rf/reg-event-fx
 ::update-questionnaire-status
 (fn [_ [_ {:keys [questionnaire status]}]]
   {:http-xhrio {:method :put
                 :uri (str "/api/questionnaires/" questionnaire "/status")
                 :params {:status (when-not (str/blank? status)
                                    status)}
                 :format http-config/transit-request-format
                 :response-format http-config/transit-response-format
                 ;; XXX Would there be some worth in having the status
                 ;; roundtripped from the backend instead of just using what
                 ;; we have here already? [Vesa M.]
                 :on-success [::update-questionnaire-status-success questionnaire status]
                 :on-failure [::ui/set-error "Failed to updated status of questionnaire"]}}))

(defn update-questionnaire-status
  [{:keys [questionnaire status] :as props}]
  (rf/dispatch [::update-questionnaire-status props]))

;; ----------------

(rf/reg-event-db
 ::set-filter-popup-visibility
 (fn [db [_ visible]]
   (assoc-in db [:question-list :filter-popup-visible] visible)))

(defn set-filter-popup-visibility
  [visible]
  (rf/dispatch [::set-filter-popup-visibility visible]))

;; ----------------

(rf/reg-event-db
 ::set-filters
 (fn [db [_ questionnaire-id filters]]
   (update-in db [:question-list :filters questionnaire-id]
              (fn [old-filters]
                (->> (merge old-filters filters)
                     (filter second)
                     (into {}))))))

(defn set-filters
  [questionnaire-id filters]
  (rf/dispatch [::set-filters questionnaire-id filters]))

;; ----------------

(rf/reg-event-db
 ::clear-filters
 (fn [db [_ questionnaire-id]]
   (update-in db [:question-list :filters questionnaire-id] dissoc "status/approved" "status/in-review" "status/unknown" "unread-notes")))

(defn clear-filters
  [questionnaire-id]
  (rf/dispatch [::clear-filters questionnaire-id]))

;; ----------------

(rf/reg-event-fx
 ::get-suggestions-success
 (fn [{:keys [db]} [_ questionnaire-id target-questionnaire-id notify? data]]
   (cond-> {:db (-> db
                    (update-in [:comparison-suggestions questionnaire-id target-questionnaire-id]
                               assoc
                               :loading false
                               :load-success true
                               :data data))}
     notify? (assoc :dispatch [::notifications/add {:message "Comparison suggestions loaded"}]))))

(rf/reg-event-fx
 ::get-suggestions-failure
 (fn [{:keys [db]} [_ questionnaire-id]]
   {:db (assoc-in db [:comparison-suggestions questionnaire-id] {:loading false
                                                                 :load-success false})
    :dispatch [::notifications/add {:message "Failed to load comparison suggestions"
                                    :class "is-danger"}]}))

(rf/reg-event-fx
 ::get-suggestions
 (fn [{:keys [db]} [_ idx questionnaire-id target-questionnaire-id]]
   (let [target-questionnaire (->> (get-in db [:questionnaire-list :data])
                                   (filter #(= target-questionnaire-id (:id %)))
                                   first)]
     {:db (-> db
              (assoc-in [:comparison-suggestions questionnaire-id target-questionnaire-id]
                        {:loading true})
              (update-in [question-list--app-db-key :comparison-control questionnaire-id :comparisons]
                         (fn [comparisons]
                           (let [max-short-ids (reduce
                                                (fn [acc {:keys [short-id target-questionnaire]}]
                                                  (update acc
                                                          (if (:template target-questionnaire)
                                                            :template
                                                            :questionnaire)
                                                          #(if (> short-id %)
                                                             short-id
                                                             %)))
                                                {:template 0
                                                 :questionnaire 0}
                                                comparisons)]
                             (->> comparisons
                                  (map-indexed
                                   (fn [comparison-idx comparison]
                                     (if (= idx comparison-idx)
                                       {:short-id (let [current-max-short-id
                                                        (get max-short-ids
                                                             (if (:template target-questionnaire)
                                                               :template
                                                               :questionnaire))]
                                                    (if (and (:target-questionnaire comparison)
                                                             (= (:template target-questionnaire)
                                                                (:template (:target-questionnaire comparison))))
                                                      (:short-id comparison)
                                                      (inc current-max-short-id)))
                                        :target-questionnaire target-questionnaire}
                                       comparison)))
                                  vec)))))
      :http-xhrio {:method :post
                   :uri (if (:template target-questionnaire)
                          "/api/suggest/template"
                          "/api/suggest/questionnaire")
                   :params {:questionnaire-id questionnaire-id
                            :target-questionnaire-id target-questionnaire-id}
                   :format http-config/transit-request-format
                   :response-format http-config/transit-response-format
                   :on-success [::get-suggestions-success questionnaire-id target-questionnaire-id true]
                   :on-failure [::get-suggestions-failure questionnaire-id target-questionnaire-id]}})))

(defn get-suggestions
  [idx questionnaire-id target-questionnaire-id]
  (rf/dispatch [::get-suggestions idx questionnaire-id target-questionnaire-id]))

;; ----------------

;; Can be used to refresh all suggestions for a questionnaire
(rf/reg-event-fx
 ::refresh-suggestions
 (fn [{:keys [db]} [_ questionnaire-id {target-questionnaire-id :id :as target-questionnaire}]]
   {:db (assoc-in db [:comparison-suggestions questionnaire-id target-questionnaire-id :loading] true)
    :http-xhrio {:method :post
                 :uri (if (:template target-questionnaire)
                        "/api/suggest/template"
                        "/api/suggest/questionnaire")
                 :params {:questionnaire-id questionnaire-id
                          :target-questionnaire-id (:id target-questionnaire)}
                 :format http-config/transit-request-format
                 :response-format http-config/transit-response-format
                 :on-success [::get-suggestions-success questionnaire-id target-questionnaire-id false]
                 :on-failure [::get-suggestions-failure questionnaire-id target-questionnaire-id]}}))

(rf/reg-event-fx
 ::refresh-comparisons
 (fn [{:keys [db]} [_ questionnaire-id]]
   (let [;; Note that if there would be a lot of comparisons present,
          ;; should probably consider limiting concurrency
         refresh-events (->> (get-in db [question-list--app-db-key :comparison-control questionnaire-id :comparisons])
                             (remove empty?)
                             (map (fn [{:keys [target-questionnaire]}]
                                    [:dispatch [::refresh-suggestions questionnaire-id target-questionnaire]])))]
     {:fx (if (seq refresh-events)
            (conj refresh-events
                  [:dispatch [::notifications/add {:message "Refreshing comparisons"}]])
            [])})))

(rf/reg-event-fx
 ::toggle-comparison-control-visible
 (fn [{:keys [db]} [_ questionnaire-id]]
   (let [{:keys [visible? comparisons]} (get-in db [question-list--app-db-key :comparison-control questionnaire-id])
         comparisons-visible? (not visible?)]
     (cond-> {:db (assoc-in db [question-list--app-db-key :comparison-control questionnaire-id :visible?] comparisons-visible?)}
       (and comparisons-visible?
            (seq (remove empty? comparisons)))
       (assoc :dispatch [::refresh-comparisons questionnaire-id])))))

(defn toggle-comparison-control-visible
  [questionnaire-id]
  (rf/dispatch [::toggle-comparison-control-visible questionnaire-id]))

;; ----------------

(rf/reg-event-fx
 ::init-comparisons
 (fn [{:keys [db]} [_ questionnaire-id]]
   (let [{:keys [visible? comparisons]} (get-in db [question-list--app-db-key :comparison-control questionnaire-id])]
     (cond-> {:db (update-in db [question-list--app-db-key :comparison-control questionnaire-id :comparisons]
                             (fn [comparisons]
                               (if comparisons
                                 comparisons
                                 [{}])))}
       (and visible?
            (seq (remove empty? comparisons)))
       (assoc :dispatch [::refresh-comparisons questionnaire-id])))))

(defn init-comparisons
  [questionnaire-id]
  (rf/dispatch [::init-comparisons questionnaire-id]))

;; ----------------

(rf/reg-event-db
 ::clear-comparison-target
 (fn [db [_ questionnaire-id comparison-target]]
   (update-in db
              [question-list--app-db-key :comparison-control questionnaire-id :comparisons]
              (fn [comparisons]
                (mapv (fn [{:keys [target-questionnaire] :as comparison}]
                        (if (= (:id comparison-target) (:id target-questionnaire))
                          {}
                          comparison))
                      comparisons)))))

(defn clear-comparison-target
  [questionnaire-id comparison-target]
  (rf/dispatch [::clear-comparison-target questionnaire-id comparison-target]))

;; ----------------

(rf/reg-event-db
 ::add-new-comparison
 (fn [db [_ questionnaire-id]]
   (update-in db [question-list--app-db-key
                  :comparison-control
                  questionnaire-id
                  :comparisons]
              (fnil conj []) {})))

(defn add-new-comparison
  [questionnaire-id]
  (rf/dispatch [::add-new-comparison questionnaire-id]))

;; ----------------

(rf/reg-event-db
 ::remove-comparison
 (fn [db [_ questionnaire-id idx]]
   (update-in db [question-list--app-db-key
                  :comparison-control
                  questionnaire-id
                  :comparisons]
              (fn [comparisons]
                (into (subvec comparisons 0 idx)
                      (subvec comparisons (inc idx) (count comparisons)))))))

(defn remove-comparison
  [questionnaire-id idx]
  (rf/dispatch [::remove-comparison questionnaire-id idx]))

;; ----------------

(rf/reg-event-fx
 ::commit-edit
 (fn [_ [_ {:keys [id selected-answer questionnaire-id] :as question} field value]]
   (let [answer-source (:source selected-answer)
         answer (if (#{"inferred_faq" "inferred_document"} answer-source)
                  ;; If the original answer was a inference result, pick up
                  ;; the fields from inference data for basis of new edit
                  (assoc selected-answer
                         :response (answer-data/get-response answer-source selected-answer)
                         :comment (answer-data/get-comment answer-source selected-answer))
                  selected-answer)
         params (-> answer
                    (assoc field value)
                    (assoc :applied-analysis-version-ids
                           (set (map :version-id (:applied-analyses answer))))
                    (dissoc :applied-analyses)
                    (assoc :source-system "webapp"))
          ;; When answer has options, keep only the selected options when editing a field
         params (if (:options params)
                  (update params :options (fn [options]
                                            (keep (fn [{:keys [id selected]}]
                                                    (when selected
                                                      {:id id}))
                                                  options)))
                  params)]
     {:http-xhrio {:method :post
                   :uri (str "/api/questions/question/" id "/answer")
                   :params params
                   :format http-config/transit-request-format
                   :response-format http-config/transit-response-format
                   :on-success [::answers/add-and-select-answer questionnaire-id question]
                   :on-failure [::ui/set-error "Failed to save edit"]}})))

(defn commit-edit
  [question field value]
  (rf/dispatch [::commit-edit question field value]))

;; ----------------

(rf/reg-event-fx
 ::add-and-select-answer
 (fn [{:keys [db]} [_
                    questionnaire-id
                    question
                    {:keys [question-id
                            applied-analysis-version-ids
                            id]
                     :as answer}]]
   (let [db (-> db
                (update-in [:questionnaires
                            questionnaire-id
                            :answers-by-question
                            question-id
                            :data]
                           conj
                           answer)
                (update-in [:questionnaires
                            questionnaire-id
                            :questions
                            :data]
                           (fn [questions]
                             (mapv (fn [question]
                                     (if (= (:id question)
                                            (:question-id answer))
                                       (assoc question :selected-answer answer)
                                       question))
                                   questions))))]
     {:db (if (seq applied-analysis-version-ids)
            (update-in
             db
             [:applied-analyses :data]
             (fn [data]
               (update-vals
                data
                (fn [applied-analyses]
                  (map (fn [{:keys [version-id] :as applied-analysis}]
                         (if (contains? applied-analysis-version-ids version-id)
                           (update applied-analysis :answer-ids (fnil conj #{}) id)
                           applied-analysis))
                       applied-analyses)))))
            db)
      :fx [[:dispatch [::notifications/add {:message "Answer created and selected"}]]
           (when (and (= schemas/not-yet-evaluated-answer-scope
                         (-> question :answer-scope :content))
                      (seq applied-analysis-version-ids))
             [:dispatch [::answers/set-answer-scope
                         questionnaire-id
                         #{(:id question)}
                         schemas/completed-by-analyst-answer-scope
                         "create-answer-with-applied-analysis"]])]})))

(rf/reg-event-fx
 ::add-and-select-answer-update-analysis
 (fn [_ [_ questionnaire-id question answer]]
   (analysis.db/link-analysis-success
    identity
    questionnaire-id
    (-> question :selected-answer :applied-analyses)
    {(:id question) answer})
   {:fx [[:dispatch [::add-and-select-answer questionnaire-id question answer]]]}))

(rf/reg-event-fx
 ::commit-update
 (fn [_ [_ {:keys [id selected-answer questionnaire-id] :as question}]]
   (let [version-ids (set (map :version-id (:applied-analyses selected-answer)))
         params (-> selected-answer
                    (assoc :applied-analysis-version-ids version-ids)
                    (dissoc :applied-analyses)
                    (assoc :source-system "webapp"))]
     {:http-xhrio {:method :post
                   :uri (str "/api/questions/question/" id "/answer")
                   :params params
                   :format http-config/transit-request-format
                   :response-format http-config/transit-response-format
                   :on-success [::add-and-select-answer-update-analysis
                                questionnaire-id
                                question]
                   :on-failure [::ui/set-error "Failed to save edit"]}})))

(defn commit-update
  [question]
  (rf/dispatch [::commit-update question]))

;; ----------------

(defn question-matches-with-local-analyses
  [questionnaire-id questions]
  (->> questions
       (keep
        (fn [question]
          (when (and (= questionnaire-id
                        (-> question :target-questionnaire :id))
                     (seq (-> question :selected-answer :applied-analyses))
                     (-> question :suggestion :question-id))
            [(:id question) (-> question :suggestion :question-id)])))
       (into {})))

(defn matching-question-count
  [questions target-questionnaire-id]
  {:matching-question-count
   (count (filter
           #(and (= target-questionnaire-id
                    (-> % :target-questionnaire :id))
                 (-> % :suggestion :question-id))
           questions))
   :question-count
   (count (filter :comparison-mode questions))})

;; ----------------

(rf/reg-event-fx
 ::push-linked-structured-analyses-success
 (fn [{:keys [db]} [_ questionnaire-id]]
   {:db (assoc-in db
                  [question-list--app-db-key
                   :comparison-control
                   questionnaire-id
                   :add-analysis-state]
                  {:loading false})
    :dispatch [::notifications/add {:message "Added analyses to template on the master"}]}))

(rf/reg-event-fx
 ::push-linked-structured-analyses-failure
 (fn [{:keys [db]} [_ questionnaire-id response]]
   {:db (assoc-in db [question-list--app-db-key
                      :comparison-control
                      questionnaire-id
                      :add-analysis-state]
                  {:loading false})
    :dispatch [::notifications/add {:message (or (-> response :response :message)
                                                 "Failed to add analyses")
                                    :class "is-danger"}]}))

(rf/reg-event-fx
 ::push-linked-structured-analyses
 (fn [{:keys [db]} [_ {:keys [id]} questions]]
   (let [my-question-id->template-question-id (question-matches-with-local-analyses id questions)]
     {:db (assoc-in db
                    [question-list--app-db-key
                     :comparison-control
                     id
                     :add-analysis-state]
                    {:loading true})
      :http-xhrio {:method :post
                   :uri (str "/api/questionnaires/" id "/link-structured-analyses/client")
                   :params my-question-id->template-question-id
                   :format http-config/transit-request-format
                   :response-format http-config/transit-response-format
                   :on-success [::push-linked-structured-analyses-success id]
                   :on-failure [::push-linked-structured-analyses-failure id]}})))

(defn push-linked-structured-analyses
  [questionnaire questions]
  (rf/dispatch [::push-linked-structured-analyses questionnaire questions]))

;; ----------------

(rf/reg-event-db
 ::init-view
 (fn [db [_ questionnaire-id]]
   (update-in db
              [question-list--app-db-key
               questionnaire-id
               :column-visibility
               :section]
              (fn [value] (or value false)))))

(defn init-view
  [questionnaire-id]
  (rf/dispatch [::init-view questionnaire-id]))

;; ----------------

(rf/reg-event-fx
 ::get-analysis-suggestions-success
 (fn [{:keys [db]} [_ questionnaire-id suggestions]]
   {:db (-> db
            (assoc-in [question-list--app-db-key
                       :analysis-suggestions
                       questionnaire-id
                       :loading]
                      false)
            (assoc-in [question-list--app-db-key
                       :analysis-suggestions
                       questionnaire-id
                       :data]
                      suggestions)
            (assoc-in [question-list--app-db-key
                       :analysis-suggestions
                       questionnaire-id
                       :visible]
                      true))
    :dispatch [::notifications/add {:message "Analysis suggestions loaded"}]}))

(rf/reg-event-fx
 ::get-analysis-suggestions-failure
 (fn [{:keys [db]} [_ questionnaire-id]]
   {:db (assoc-in db [question-list--app-db-key
                      :analysis-suggestions
                      questionnaire-id
                      :loading]
                  false)
    :dispatch [::notifications/add {:message "Failed to load analysis suggestions"
                                    :class "is-danger"}]}))

(rf/reg-event-fx
 ::get-analysis-suggestions
 (fn [{:keys [db]} [_ questionnaire-id]]
   {:db (assoc-in db [question-list--app-db-key
                      :analysis-suggestions
                      questionnaire-id
                      :loading]
                  true)
    :http-xhrio {:method :get
                 :uri "/api/suggest/analyses"
                 :url-params {:questionnaire-id questionnaire-id}
                 :format http-config/transit-request-format
                 :response-format http-config/transit-response-format
                 :on-success [::get-analysis-suggestions-success questionnaire-id]
                 :on-failure [::get-analysis-suggestions-failure questionnaire-id]}}))

(defn get-analysis-suggestions
  [questionnaire-id]
  (rf/dispatch [::get-analysis-suggestions questionnaire-id]))

;; ----------------

(rf/reg-event-db
 ::hide-analysis-suggestions
 (fn [db [_ questionnaire-id]]
   (assoc-in db
             [question-list--app-db-key
              :analysis-suggestions
              questionnaire-id
              :visible]
             false)))

(defn hide-analysis-suggestions
  [questionnaire-id]
  (rf/dispatch [::hide-analysis-suggestions questionnaire-id]))

;; ----------------

(rf/reg-event-fx
 ::report-analysis-suggestion
 (fn [_ [_ content construct liked]]
   {:http-xhrio {:method :post
                 :uri "/api/suggest/report-suggestion"
                 :params {:question-content content
                          :construct construct
                          :liked liked}
                 :format http-config/transit-request-format
                 :response-format http-config/transit-response-format
                 :on-success [::notifications/add {:message "Report saved"}]
                 :on-failure [::notifications/add {:message "Failed to save report"
                                                   :class "is-danger"}]}}))

(defn report-analysis-suggestion
  [content construct liked]
  (rf/dispatch [::report-analysis-suggestion content construct liked]))

;; ----------------

(defn perform-update-question [db questionnaire-id question-id fields]
  (update-in db
             [:questionnaires questionnaire-id :questions :data]
             (fn [questions]
               (mapv (fn [{:keys [id] :as question}]
                       (if (= question-id id)
                         (merge question fields)
                         question))
                     questions))))

;; ----------------

(rf/reg-event-fx
 ::update-question
 (fn [{:keys [db]} [_ questionnaire-id question-id fields original notify?]]
   {:db (-> (perform-update-question db questionnaire-id question-id fields)
            (assoc-in [:browsers-extension
                       :question-list
                       :question-restore-state
                       question-id]
                      original))
    :http-xhrio {:method :post
                 :uri (str "/api/questions/question/" question-id)
                 :params {:original original :changes fields}
                 :timeout http-config/timeout
                 :format http-config/transit-request-format
                 :response-format http-config/transit-response-format
                 ;:on-success [::update-question-success question-id notify?]
                 :on-failure [::update-question-failure questionnaire-id question-id notify?]}}))

(defn update-question
  [questionnaire-id question-id fields original notify?]
  (rf/dispatch [::update-question questionnaire-id question-id fields original notify?]))